// Dedicated Fan profile — fan/customer operations view. Deliberately does NOT
// reuse performer-oriented CRM 360 (no cast links, studio collabs, or
// content-production workflow). Shows platform handles, purchase history
// (from the shared content-360 buyer-attribution feed), messaging history,
// segmentation, preferences, and an AI Manager outreach suggestion when one
// exists for this contact.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X as XIcon, Mail, Phone, Tag, MessageCircle, DollarSign, Sparkles, Plus, Check, Trash2, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { FOLLOWUP_KINDS, FU_DIRECTIONS, FU_OUTCOMES, STATUS_HEX, STATUS_LABEL, getRecordStatuses } from "./constants";
import { formatRelative } from "./helpers";
import OperationsPulse from "../../components/OperationsPulse";

const REPORTED_METRIC_LABELS = {
  reported_value: "Reported value",
  days_subscribed: "Days subscribed",
  subscription_count: "Subscriptions",
  subscription_revenue: "Subscription spend",
  store_count: "Store purchases",
  store_revenue: "Store spend",
  tips_revenue: "Tips",
  likes_count: "Likes",
  reblogs_count: "Reblogs",
  favorites_count: "Favorites",
};

export default function FanProfile({ person, onClose, onEdit, onDelete, onLocalUpdate }) {
  const [purchases, setPurchases] = useState(null);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [logForm, setLogForm] = useState({ kind: "dm", direction: "outbound", outcome: "neutral", summary: "", note: "" });
  const [savingFu, setSavingFu] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/api/crm/${person.id}/content-360`)
      .then((r) => { if (active) setPurchases(r.data); })
      .catch(() => { if (active) setPurchases(null); })
      .finally(() => { if (active) setLoadingPurchases(false); });
    api.get("/api/ai-manager/contact-recommendations")
      .then((r) => {
        if (!active) return;
        const match = (r.data?.recommendations || []).find((rec) => rec.contact_id === person.id);
        setAiSuggestion(match || null);
      })
      .catch(() => { if (active) setAiSuggestion(null); });
    return () => { active = false; };
  }, [person.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fuSorted = [...(person.follow_ups || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const recordStatuses = getRecordStatuses(person);
  const rawGrossTotal = purchases?.totals?.of_purchases_gross_usd;
  const grossTotal = rawGrossTotal !== null && rawGrossTotal !== undefined && Number.isFinite(Number(rawGrossTotal))
    ? Number(rawGrossTotal) : null;
  const purchaseCount = purchases?.totals?.of_purchases_count ?? null;
  const platformHandles = [
    person.onlyfans && { label: `OF ${person.onlyfans}`, color: "#00AFF0" },
    person.justforfans && { label: `JFF ${person.justforfans}`, color: "#f59e0b" },
    person.twitter && { label: `X @${person.twitter.replace(/^@/, "")}`, color: "#e5e7eb" },
    person.instagram && { label: `IG @${person.instagram.replace(/^@/, "")}`, color: "#ec4899" },
  ].filter(Boolean);

  const persistFollowUps = async (next) => {
    try {
      const updated = await api.put(`/api/crm/${person.id}`, { follow_ups: next });
      onLocalUpdate(updated.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const addFollowUp = async () => {
    const summary = (logForm.summary || "").trim();
    if (!summary) return toast.error("Summary is required");
    setSavingFu(true);
    const newFu = {
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      kind: logForm.kind, direction: logForm.direction, outcome: logForm.outcome,
      summary, note: (logForm.note || "").trim(), date: new Date().toISOString(), done: false,
    };
    await persistFollowUps([...(person.follow_ups || []), newFu]);
    setLogForm({ kind: "dm", direction: "outbound", outcome: "neutral", summary: "", note: "" });
    setShowLog(false);
    setSavingFu(false);
    toast.success("Interaction logged");
  };

  const toggleFollowUp = async (id) => {
    const next = (person.follow_ups || []).map((it) => (it.id === id ? { ...it, done: !it.done } : it));
    await persistFollowUps(next);
  };

  const deleteFollowUp = async (id) => {
    await persistFollowUps((person.follow_ups || []).filter((it) => it.id !== id));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center" onClick={onClose} data-testid="fan-profile">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full md:max-w-3xl mx-auto md:my-6 md:rounded-2xl overflow-hidden flex flex-col max-h-[100vh] md:max-h-[92vh]"
        style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold shrink-0"
              style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
              {(person.stage_name || "?")[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-heading text-xl truncate" style={{ color: "var(--gio-text)" }}>{person.stage_name}</div>
              <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>Fan / customer record</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {recordStatuses.map((status) => {
                  const color = STATUS_HEX[status] || "#94a3b8";
                  return <span key={status} className="rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ color, borderColor: `${color}66`, background: `${color}18` }}>
                    {STATUS_LABEL[status] || status.replace(/_/g, " ")}
                  </span>;
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={onEdit} data-testid="fan-profile-edit" className="btn-ghost text-xs">Edit</button>
            <button onClick={onDelete} data-testid="fan-profile-delete" className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
            <button onClick={onClose} data-testid="fan-profile-close" className="text-zinc-500 hover:text-white"><XIcon size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-5">
            <OperationsPulse
              title="Fan operations pulse"
              subtitle="Purchases, engagement history, and outreach status for this fan."
              accent="#00AFF0"
              metrics={[
                { label: "Linked purchases", value: purchaseCount ?? "Not available", color: "#22c55e", detail: purchaseCount ? "attributed" : "not linked" },
                { label: "Gross OnlyFans spend", value: grossTotal === null ? "Not available" : `$${grossTotal.toFixed(2)}`, color: "#22c55e", detail: "linked purchases only · USD" },
                { label: "Interactions", value: (person.follow_ups || []).length, detail: "messaging history" },
                { label: "Segments", value: (person.tags || []).length, color: "#a855f7", detail: "tags" },
              ]}
              gap="Subscription/platform status, retention, and conversion analytics are not available from the current shared data contract; only linked purchase records are shown."
            />

            {platformHandles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {platformHandles.map((h) => (
                  <span key={h.label} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: `${h.color}55`, color: h.color }}>{h.label}</span>
                ))}
              </div>
            )}
            {platformHandles.length === 0 && (
              <div className="text-[12px] italic mb-5" style={{ color: "var(--gio-muted-3)" }}>No platform handles linked yet.</div>
            )}

            <Section title={`Verified source identities · ${(person.source_identities || []).length}`} icon={<ExternalLink size={13} />}>
              {(person.source_identities || []).length === 0 && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>No stable platform identity is linked yet.</div>}
              <div className="space-y-2">
                {(person.source_identities || []).map((identity) => {
                  const reportedMetrics = Object.entries(identity.reported_metrics || {})
                    .filter(([key, value]) => REPORTED_METRIC_LABELS[key] && Number.isFinite(Number(value)));
                  return <div key={`${identity.platform}:${identity.account_id}:${identity.native_fan_id}`} className="border p-3 text-xs" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <strong style={{ color: "var(--gio-text)" }}>{identity.platform} · {identity.account_id}</strong>
                    {identity.profile_url && <a href={identity.profile_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-300"><ExternalLink size={11} />Source profile</a>}
                  </div>
                  <dl className="mt-2 grid grid-cols-1 gap-1 text-[11px] sm:grid-cols-2" style={{ color: "var(--gio-muted-2)" }}>
                    <div>Username: {identity.username ? `@${identity.username}` : "Unknown"}</div>
                    <div>Native ID: {identity.native_fan_id || "Unknown"}</div>
                    <div>Subscription: {identity.subscription_status || "Unknown"}</div>
                    <div>Subscription type: {identity.subscription_type || "Unknown"}</div>
                    <div>Started: {identity.subscription_started_at || "Unknown"}</div>
                    <div>Ends: {identity.subscription_ends_at || "Unknown"}</div>
                    <div>First observed: {identity.first_observed_at || "Unknown"}</div>
                    <div>Last observed: {identity.last_observed_at || "Unknown"}</div>
                    <div>Last synced: {identity.extraction_time || "Unknown"}</div>
                  </dl>
                  {reportedMetrics.length > 0 && <div className="mt-3 border-t pt-2" style={{ borderColor: "var(--gio-border)" }}>
                    <div className="mb-2 text-[10px] font-semibold uppercase" style={{ color: "var(--gio-muted-3)" }}>Reported subscriber metrics{identity.reported_metrics_currency ? ` · ${identity.reported_metrics_currency}` : ""}</div>
                    <dl className="grid grid-cols-2 gap-1 text-[11px]" style={{ color: "var(--gio-muted-2)" }}>
                      {reportedMetrics.map(([key, value]) => {
                        const isMoney = /revenue$|^reported_value$/.test(key);
                        const formatted = isMoney && identity.reported_metrics_currency
                          ? new Intl.NumberFormat(undefined, { style: "currency", currency: identity.reported_metrics_currency }).format(Number(value))
                          : Number(value).toLocaleString();
                        return <div key={key}>{REPORTED_METRIC_LABELS[key]}: {formatted}</div>;
                      })}
                    </dl>
                    <div className="mt-2 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Platform snapshot only; not reconciled to Finance purchases.</div>
                  </div>}
                  </div>;
                })}
              </div>
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 text-sm">
              {person.email && <div className="inline-flex items-center gap-1.5" style={{ color: "var(--gio-muted-2)" }}><Mail size={12} /> {person.email}</div>}
              {person.phone && <div className="inline-flex items-center gap-1.5" style={{ color: "var(--gio-muted-2)" }}><Phone size={12} /> {person.phone}</div>}
            </div>

            {aiSuggestion && (
              <div className="rounded-lg border p-4 mb-5" style={{ borderColor: "rgba(168,85,247,0.4)", background: "rgba(168,85,247,0.06)" }} data-testid="fan-ai-suggestion">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: "#a855f7" }}>
                  <Sparkles size={13} /> AI Manager suggestion
                </div>
                <div className="text-sm" style={{ color: "var(--gio-text)" }}>{aiSuggestion.reasoning}</div>
                <div className="text-[11px] mt-1" style={{ color: "var(--gio-muted-2)" }}>Priority: {aiSuggestion.priority} · Suggested tone: {aiSuggestion.suggested_tone}</div>
              </div>
            )}

            {/* Purchase history — real linked data only */}
            <Section title="Purchase history" icon={<DollarSign size={13} />}>
              {loadingPurchases && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
              {!loadingPurchases && (purchases?.of_sales || []).length === 0 && (
                <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>No purchases linked to this fan yet — not a failure, just not attributed.</div>
              )}
              {(purchases?.of_sales || []).length > 0 && (
                <div className="space-y-1.5" data-testid="fan-purchase-list">
                  {purchases.of_sales.slice(0, 20).map((s) => (
                    <div key={s.id} className="border rounded p-2 flex items-center gap-3 text-[11px]" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
                      <span style={{ color: "var(--gio-muted-2)" }}>{(s.date || "").slice(0, 10)}</span>
                      <span className="uppercase font-bold" style={{ color: "var(--gio-secondary)" }}>{s.platform}</span>
                      <span className="flex-1 truncate" style={{ color: "var(--gio-muted-2)" }}>{s.item || s.type || "purchase"}</span>
                      <span className="font-bold" style={{ color: "#22c55e" }}>${s.gross_usd}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Segmentation */}
            <Section title="Segments" icon={<Tag size={13} />}>
              {(person.tags || []).length === 0 && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>No segments set.</div>}
              {(person.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {person.tags.map((t) => <span key={t} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted)" }}>{t}</span>)}
                </div>
              )}
            </Section>

            {(person.kinks || person.boundaries || person.notes) && (
              <Section title="Preferences &amp; notes" icon={<MessageCircle size={13} />}>
                <div className="grid md:grid-cols-3 gap-3 text-[12px]">
                  {person.kinks && <div><div className="font-bold mb-1" style={{ color: "var(--gio-muted)" }}>Preferences / kinks</div><div style={{ color: "var(--gio-muted-2)" }}>{person.kinks}</div></div>}
                  {person.boundaries && <div><div className="font-bold mb-1" style={{ color: "var(--gio-muted)" }}>Boundaries / limits</div><div style={{ color: "var(--gio-muted-2)" }}>{person.boundaries}</div></div>}
                  {person.notes && <div><div className="font-bold mb-1" style={{ color: "var(--gio-muted)" }}>Notes</div><div style={{ color: "var(--gio-muted-2)" }}>{person.notes}</div></div>}
                </div>
              </Section>
            )}

            {/* Messaging / DM history */}
            <Section title={`Messaging history · ${fuSorted.length}`} icon={<MessageCircle size={13} />}
              action={<button onClick={() => setShowLog((v) => !v)} data-testid="fan-log-toggle" className="text-xs px-2.5 py-1 rounded border" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}><Plus size={11} className="inline mr-1" />Log</button>}>
              {showLog && (
                <div className="mb-3 p-3 rounded-lg border space-y-2" style={{ borderColor: "var(--gio-border)" }}>
                  <div className="flex gap-2 flex-wrap">
                    {FOLLOWUP_KINDS.map((k) => (
                      <button key={k.v} onClick={() => setLogForm((f) => ({ ...f, kind: k.v }))} type="button"
                        className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border"
                        style={{ borderColor: logForm.kind === k.v ? "var(--gio-primary)" : "var(--gio-border)", color: logForm.kind === k.v ? "var(--gio-primary)" : "var(--gio-muted-2)" }}>
                        {k.label}
                      </button>
                    ))}
                  </div>
                  <input value={logForm.summary} onChange={(e) => setLogForm((f) => ({ ...f, summary: e.target.value }))}
                    placeholder="Subject / summary" data-testid="fan-log-summary" className="gio-input w-full" />
                  <textarea value={logForm.note} onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="Details" rows={2} data-testid="fan-log-note" className="gio-input w-full" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowLog(false)} className="btn-ghost text-xs">Cancel</button>
                    <button onClick={addFollowUp} disabled={savingFu} data-testid="fan-log-save" className="btn-primary text-xs">Save</button>
                  </div>
                </div>
              )}
              {fuSorted.length === 0 && <div className="text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>No messaging history logged yet.</div>}
              <div className="space-y-1.5">
                {fuSorted.map((f) => {
                  const k = FOLLOWUP_KINDS.find((x) => x.v === f.kind) || FOLLOWUP_KINDS[0];
                  const dir = FU_DIRECTIONS.find((d) => d.v === f.direction);
                  const outcome = FU_OUTCOMES.find((o) => o.v === f.outcome);
                  return (
                    <div key={f.id} className="flex items-start gap-2 p-2 border rounded" style={{ borderColor: "var(--gio-border)" }}>
                      <button onClick={() => toggleFollowUp(f.id)} className="mt-0.5 w-4 h-4 rounded border shrink-0"
                        style={{ borderColor: f.done ? "#22c55e" : "var(--gio-border)", background: f.done ? "#22c55e" : "transparent" }}>
                        {f.done && <Check size={11} style={{ color: "#fff" }} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[12px] ${f.done ? "line-through" : ""}`} style={{ color: "var(--gio-text)" }}>{f.summary}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: "var(--gio-muted-3)" }}>
                          {k.label} {dir && `· ${dir.label.split(" ")[0]}`} {outcome && `· ${outcome.label}`} · {formatRelative(f.date)}
                        </div>
                      </div>
                      <button onClick={() => deleteFollowUp(f.id)} className="text-zinc-600 hover:text-red-400 shrink-0"><Trash2 size={12} /></button>
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, action, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>{icon}{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
